/**
 * Authenticated app chrome.
 *
 * Picks desktop vs mobile layout via ``useMediaQuery("(max-width: 700px)")``.
 * The route tree decides which screen renders inside the ``<Outlet />``.
 * Per-app accent comes from the URL's first segment.
 */

import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useApi } from "../auth/AuthProvider";
import { ymd } from "../calendar/useCalendar";
import { useMediaQuery } from "../lib/useMediaQuery";
import { useT } from "../lib/i18n";
import { MentionProvider } from "../lib/MentionContext";
import type { MentionKind } from "../lib/MentionContext";
import { fetchEventRef, type EventRef } from "../lib/useEntityRef";
import { findAppMeta, type AppId } from "./apps";
import { mentionTo, type MentionToOpts } from "./mentionTo";
import { DesktopTopbar } from "./DesktopTopbar";
import { Drawer } from "./Drawer";
import { MobileTabbar } from "./MobileTabbar";
import { MobileTopbar } from "./MobileTopbar";
import { NewActionProvider } from "./NewActionContext";
import { ScreenContainer } from "./ScreenContainer";
import { Sidebar } from "./Sidebar";

const MOBILE_QUERY = "(max-width: 700px)";

/** Maps the first path segment to an ``AppId | "settings"``. */
function appIdFromPath(pathname: string): AppId | "settings" {
  const seg = pathname.split("/")[1] ?? "";
  switch (seg) {
    case "":
    case "today":
      return "today";
    case "tasks":
      return "tasks";
    case "notes":
      return "notes";
    case "calendar":
      return "cal";
    case "habits":
      return "habit";
    case "shopping":
      return "shop";
    case "recipes":
      return "cook";
    case "people":
      return "people";
    case "settings":
      return "settings";
    default:
      return "today";
  }
}

export function AppShell(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const client = useApi();
  const compact = useMediaQuery(MOBILE_QUERY);
  const [, T] = useT();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const screenId = appIdFromPath(location.pathname);
  const meta = findAppMeta(screenId);
  const accentVar = `var(${meta.tint})`;

  const onMentionClick = async (kind: MentionKind, id: string): Promise<void> => {
    // For event mentions, try to pull the date (and, for a recurring
    // event, its next occurrence identity) from React Query's cache so
    // the URL lands on the right day/occurrence. If the event hasn't
    // been hydrated yet, fetch it once; mentionTo falls back to
    // /calendar if we still can't resolve a date. A failed fetch (404,
    // network, auth) must not turn into an unhandled rejection or a
    // silently-dead click — fall back to the bare /calendar link.
    let opts: MentionToOpts | undefined;
    if (kind === "event") {
      try {
        let cached = qc.getQueryData<EventRef>(["entity", "event", id]);
        if (cached === undefined) {
          cached = await qc.fetchQuery<EventRef>({
            queryKey: ["entity", "event", id],
            queryFn: () => fetchEventRef(client, id),
          });
        }
        if (cached.next_occurrence_at && cached.next_occurrence_start) {
          opts = { eventDate: ymd(new Date(cached.next_occurrence_at)), occ: cached.next_occurrence_start };
        } else if (cached.starts_at !== undefined) {
          opts = { eventDate: ymd(new Date(cached.starts_at)) };
        }
      } catch {
        opts = undefined;
      }
    }
    void navigate(mentionTo(kind, id, opts));
    setDrawerOpen(false);
  };

  const screenEl = (
    <MentionProvider value={{ onMentionClick }}>
      <Outlet />
    </MentionProvider>
  );

  if (compact) {
    return (
      <NewActionProvider>
        <div
          className="vz-app vz-col"
          style={{
            ["--app-accent" as string]: accentVar,
            ["--app-tint" as string]: accentVar,
            position: "relative",
          }}
        >
          <MobileTopbar
            title={T(meta.i18nKey)}
            appTint={meta.tint}
            onMenu={() => setDrawerOpen((d) => !d)}
            onSettings={() => navigate("/settings")}
          />
          <ScreenContainer>{screenEl}</ScreenContainer>
          <MobileTabbar />
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
            <Sidebar />
          </Drawer>
        </div>
      </NewActionProvider>
    );
  }

  return (
    <NewActionProvider>
      <div
        className="vz-app vz-row"
        style={{
          ["--app-accent" as string]: accentVar,
          ["--app-tint" as string]: accentVar,
          alignItems: "stretch",
        }}
      >
        <Sidebar />
        <div className="vz-main">
          <DesktopTopbar appMeta={meta} />
          <ScreenContainer>{screenEl}</ScreenContainer>
        </div>
      </div>
    </NewActionProvider>
  );
}
